import type { IfcSchemaVersion } from '@viewer/store/viewerSlice'

const SCHEMA_REGEX = /FILE_SCHEMA\s*\(\s*\(\s*'([^']+)'/i

const KNOWN_SCHEMAS: ReadonlyArray<IfcSchemaVersion> = [
  'IFC2X3',
  'IFC4',
  'IFC4X1',
  'IFC4X2',
  'IFC4X3',
]

// Reads the IFC STEP header and returns the schema identifier. The IFC
// header is plain text near the start of the file so we only need to
// decode the first few KB. Handles IFC 2x3 through 4x3 (including
// IFC4X3_ADD1 / IFC4X3_ADD2, which both map to IFC4X3).
export function readSchemaFromIfcBytes(
  bytes: Uint8Array,
): IfcSchemaVersion | null {
  const sliceEnd = Math.min(bytes.length, 8192)
  const header = new TextDecoder('ascii', { fatal: false }).decode(
    bytes.subarray(0, sliceEnd),
  )
  const match = SCHEMA_REGEX.exec(header)
  if (!match) return null
  const raw = match[1].toUpperCase()
  if (raw.startsWith('IFC4X3')) return 'IFC4X3'
  for (const known of KNOWN_SCHEMAS) {
    if (raw === known) return known
  }
  return null
}

/** Porque é que estes bytes não podem ir para o conversor, ou null se
 *  podem. Só apanha o que se prova à cabeça: ficheiro vazio, ou um
 *  ficheiro que não começa como um STEP (ISO 10303-21), que é o que
 *  qualquer IFC em texto é.
 *
 *  Sem isto, o web-ifc recebia-os na mesma e falhava mal: um ficheiro
 *  vazio dava "memory access out of bounds", e alguns bytes de texto
 *  qualquer prendiam a página perto de meio minuto antes de darem em
 *  nada. A pessoa ficava sem saber que o problema era o ficheiro. */
export function motivoParaRecusarIfc(bytes: Uint8Array, nome: string): string | null {
  if (bytes.length === 0) {
    return `"${nome}" está vazio (0 bytes). Confirma que o ficheiro acabou de ser gravado ou descarregado.`
  }
  // O BOM do UTF-8 e espaços antes do cabeçalho não são da norma, mas
  // aparecem em ficheiros reais e o conversor aceita-os.
  let i = 0
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) i = 3
  while (i < bytes.length && i < 1024 && (bytes[i] === 0x20 || bytes[i] === 0x09 || bytes[i] === 0x0a || bytes[i] === 0x0d)) i++
  const inicio = new TextDecoder('ascii', { fatal: false }).decode(bytes.subarray(i, i + 12))
  if (inicio.toUpperCase() !== 'ISO-10303-21') {
    return `"${nome}" não é um ficheiro IFC: não começa pelo cabeçalho ISO-10303-21 que todos os IFC em texto têm.`
  }
  return null
}

const SCHEMA_LABELS: Record<IfcSchemaVersion, string> = {
  IFC2X3: 'IFC 2x3',
  IFC4: 'IFC 4',
  IFC4X1: 'IFC 4.1',
  IFC4X2: 'IFC 4.2',
  IFC4X3: 'IFC 4.3',
}

export function schemaLabel(schema: IfcSchemaVersion | null): string {
  if (!schema) return 'Schema desconhecido'
  return SCHEMA_LABELS[schema]
}
